import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import moment from "moment";

/*
 * Modal para armar el INSERT de los registros eliminados (ACCIÓN = DELETE) que
 * se consultan en el modulo de Auditoria.
 *
 * Cada registro eliminado guarda en `valor_anterior` (jsonb) la fila completa
 * que se borro de la tabla indicada en `tabla_afectada`. El modal muestra esos
 * datos agrupados por tabla, permite seleccionar las filas y arma el INSERT que
 * se copia para ejecutarlo en la base de datos y restaurarlas.
 *
 * Recibe:
 *  - show:          muestra u oculta el modal
 *  - registros:     arreglo con los registros de auditoria de accion DELETE
 *  - onHideCancela: cierra el modal
 */

const esObjeto = (valor) =>
  valor !== null && typeof valor === "object" && !Array.isArray(valor);

/*
 * Los registros de auditoria pueden venir envueltos en el nombre de la tabla o
 * en el tipo de dato, por ejemplo:
 *   { data_nueva: { facturacompra: { numfactura: '8415332552', ... } } }
 *   { facturacompra: { numfactura: '8415332552', ... } }
 * Esta funcion quita esas envolturas (hasta 3 niveles), igual que el modulo de
 * auditoria, para quedarse con los campos reales de la fila.
 */
const desenvolver = (valor, nivel) => {
  const nivelActual = nivel || 0;

  if (nivelActual >= 3 || !esObjeto(valor)) {
    return valor;
  }

  const claves = Object.keys(valor);
  if (claves.length !== 1) {
    return valor;
  }

  const interno = valor[claves[0]];
  return esObjeto(interno) ? desenvolver(interno, nivelActual + 1) : valor;
};

/* Texto de una celda de la tabla (los objetos se muestran como JSON) */
const valorTexto = (valor) => {
  if (valor === null || valor === undefined) {
    return "";
  }
  if (typeof valor === "object") {
    try {
      return JSON.stringify(valor);
    } catch (error) {
      return String(valor);
    }
  }
  return String(valor);
};

/* Valor de un campo de la fila (sin romper si la fila no es un objeto) */
const datoDe = (fila, columna) =>
  esObjeto(fila.datos) ? fila.datos[columna] : undefined;

/* Literal SQL de un valor: NULL, numero, booleano o texto entre comillas */
const valorSql = (valor) => {
  if (valor === null || valor === undefined) {
    return "NULL";
  }
  if (typeof valor === "boolean") {
    return valor ? "true" : "false";
  }
  if (typeof valor === "number") {
    return String(valor);
  }
  const texto =
    typeof valor === "object" ? JSON.stringify(valor) : String(valor);
  return "'" + texto.replace(/'/g, "''") + "'";
};

/*
 * Identificador SQL (tabla o columna). Se entrecomilla siempre para conservar
 * el nombre exacto del campo y evitar problemas con palabras reservadas.
 */
const identificador = (nombre) =>
  String(nombre)
    .split(".")
    .map((parte) => '"' + parte.replace(/"/g, '""') + '"')
    .join(".");

/* Fecha del registro de auditoria */
const formatearFecha = (valor) =>
  valor ? moment(valor).format("DD-MM-YYYY hh:mm A") : "";

/* ------------------------- fin de los ayudantes ------------------------- */

export const RestaurarAuditoria = (props) => {
  const { show, registros, onHideCancela } = props;
  const [seleccion, setSeleccion] = useState({});
  const [sql, setSql] = useState("");
  const [copiado, setCopiado] = useState(false);
  const [aviso, setAviso] = useState("");
  const sqlRef = useRef(null);

  const lista = Array.isArray(registros) ? registros : [];

  /* Al abrir se seleccionan todos los registros y se limpia el INSERT */
  useEffect(() => {
    if (!show) {
      return;
    }
    const inicial = {};
    lista.forEach((item, indice) => {
      inicial[indice] = true;
    });
    setSeleccion(inicial);
    setSql("");
    setCopiado(false);
    setAviso("");
  }, [show, registros]);

  /* Al armar el INSERT se lleva la vista al bloque del SQL */
  useEffect(() => {
    if (sql && sqlRef.current) {
      sqlRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [sql]);

  /*
   * Agrupa los registros por tabla afectada. De cada registro se toma el valor
   * anterior (o `data` en la pestaña "Auditoría") ya desenvuelto, y se calculan
   * las columnas como la union de los campos de todas las filas del grupo.
   */
  const grupos = [];
  const indiceGrupos = {};
  lista.forEach((item, indice) => {
    const tabla =
      String(item.tabla_afectada || item.nombretabla || "tabla").trim() ||
      "tabla";

    if (!indiceGrupos[tabla]) {
      indiceGrupos[tabla] = { tabla, filas: [], columnas: [] };
      grupos.push(indiceGrupos[tabla]);
    }

    const grupo = indiceGrupos[tabla];
    const crudo =
      item.valor_anterior !== undefined && item.valor_anterior !== null
        ? item.valor_anterior
        : item.data;
    const datos = desenvolver(crudo);

    grupo.filas.push({
      indice,
      fecha: formatearFecha(item.fecha_ve),
      registro:
        item.registro_id !== undefined && item.registro_id !== null
          ? item.registro_id
          : item.idtabla,
      datos,
    });

    if (esObjeto(datos)) {
      Object.keys(datos).forEach((clave) => {
        if (grupo.columnas.indexOf(clave) < 0) {
          grupo.columnas.push(clave);
        }
      });
    }
  });

  const totalSeleccionados = lista.filter(
    (item, indice) => seleccion[indice]
  ).length;

  const alternar = (indice) => (e) => {
    const marcado = e.target.checked;
    setSeleccion((anteriores) => ({ ...anteriores, [indice]: marcado }));
  };

  const alternarGrupo = (filas, marcado) => () => {
    setSeleccion((anteriores) => {
      const siguiente = { ...anteriores };
      filas.forEach((fila) => {
        siguiente[fila.indice] = marcado;
      });
      return siguiente;
    });
  };

  /* Arma el INSERT de las filas seleccionadas, agrupadas por tabla */
  const armarInsert = () => {
    const sentencias = [];
    const sinDatos = [];

    grupos.forEach((grupo) => {
      const elegidas = grupo.filas.filter((fila) => seleccion[fila.indice]);
      if (elegidas.length === 0) {
        return;
      }

      const columnas = [];
      const filasValidas = [];

      elegidas.forEach((fila) => {
        if (!esObjeto(fila.datos) || Object.keys(fila.datos).length === 0) {
          sinDatos.push(
            grupo.tabla + " (" + valorTexto(fila.registro) + ")"
          );
          return;
        }
        Object.keys(fila.datos).forEach((clave) => {
          if (columnas.indexOf(clave) < 0) {
            columnas.push(clave);
          }
        });
        filasValidas.push(fila);
      });

      if (filasValidas.length === 0) {
        return;
      }

      const valores = filasValidas.map(
        (fila) =>
          "  (" +
          columnas.map((clave) => valorSql(fila.datos[clave])).join(", ") +
          ")"
      );

      sentencias.push(
        "INSERT INTO " +
        identificador(grupo.tabla) +
        " (" +
        columnas.map(identificador).join(", ") +
        ") VALUES\n" +
        valores.join(",\n") +
        ";"
      );
    });

    if (sentencias.length === 0) {
      setSql("");
      setAviso(
        "No hay datos en el valor anterior de los registros seleccionados."
      );
      return;
    }

    setAviso(
      sinDatos.length > 0
        ? "Se omitieron " +
        sinDatos.length +
        " registro(s) sin datos en el valor anterior: " +
        sinDatos.join(", ") +
        "."
        : ""
    );
    setSql(sentencias.join("\n\n"));
  };

  const copiarSql = () => {
    try {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(sql);
      }
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch (error) {
      /* si el navegador no permite copiar no se hace nada */
    }
  };

  /* --------------------------- fin de la logica --------------------------- */

  return (
    <Modal
      show={show}
      onHide={onHideCancela}
      size="xl"
      dialogClassName="modal-auditoria-detalle"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      keyboard={false}
      scrollable
    >
      <Modal.Header className="azulGradiant header-modal">
        <div className="d-flex align-items-center">
          <i className="fas fa-trash-restore fa-fw me-2 fs-4 text-light"></i>
          <div className="text-light">
            <h6 className="mb-0 fw-bold">
              Restaurar registros eliminados (INSERT)
            </h6>
            <small className="fw-normal">
              ACCIÓN DELETE · {lista.length} registro
              {lista.length === 1 ? "" : "s"} · {totalSeleccionados} seleccionado
              {totalSeleccionados === 1 ? "" : "s"}
            </small>
          </div>
        </div>
        <button
          className="bg-transparent"
          style={{ border: 0, margin: 0, padding: 0, color: "#ffffff" }}
          onClick={onHideCancela}
        >
          <i className="far fa-window-close"></i>
        </button>
      </Modal.Header>

      <Modal.Body className="bg-light">
        {lista.length === 0 ? (
          <p className="text-center mb-0">Sin datos.</p>
        ) : (
          <div className="container-fluid px-0">
            <span className="aud-seccion-titulo d-block">
              Datos eliminados (valor anterior)
            </span>
            <p className="small text-muted mb-2">
              Seleccione las filas que desea restaurar. La columna TABLA indica
              la tabla afectada y las demás columnas son los campos guardados en
              el valor anterior de cada registro de auditoría.
            </p>

            {grupos.map((grupo) => {
              const marcadas = grupo.filas.filter(
                (fila) => seleccion[fila.indice]
              ).length;
              const todos = marcadas === grupo.filas.length;

              return (
                <div className="mb-3" key={grupo.tabla}>
                  <div className="aud-ticket-cabecera">
                    <i className="fas fa-table me-1"></i>
                    <span>{grupo.tabla}</span>
                    <span className="ms-1 fw-normal">
                      ({grupo.filas.length})
                    </span>
                    <span className="ms-auto fw-normal">
                      {marcadas} seleccionado{marcadas === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div className="aud-ticket-tabla-wrap">
                    <table className="aud-ticket-tabla">
                      <thead>
                        <tr>
                          <th className="aud-col-check">
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={todos}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate = !todos && marcadas > 0;
                                }
                              }}
                              onChange={alternarGrupo(grupo.filas, !todos)}
                              title="Seleccionar todo el grupo"
                            />
                          </th>
                          <th>FECHA</th>
                          <th>REGISTRO</th>
                          {grupo.columnas.map((columna) => (
                            <th key={columna}>{columna}</th>
                          ))}
                          {grupo.columnas.length === 0 && (
                            <th>VALOR ANTERIOR</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.filas.map((fila) => (
                          <tr
                            key={fila.indice}
                            className={
                              seleccion[fila.indice] ? "aud-fila-marcada" : ""
                            }
                          >
                            <td className="aud-col-check">
                              <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={!!seleccion[fila.indice]}
                                onChange={alternar(fila.indice)}
                              />
                            </td>
                            <td>{fila.fecha}</td>
                            <td>{valorTexto(fila.registro)}</td>
                            {grupo.columnas.map((columna) => (
                              <td
                                key={columna}
                                className="aud-celda-dato"
                                title={valorTexto(datoDe(fila, columna))}
                              >
                                {valorTexto(datoDe(fila, columna))}
                              </td>
                            ))}
                            {grupo.columnas.length === 0 && (
                              <td className="text-muted">Sin datos</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {aviso ? (
              <div className="alert alert-warning py-1 px-2 small mb-2">
                <i className="fas fa-exclamation-triangle me-1"></i>
                {aviso}
              </div>
            ) : null}

            {sql ? (
              <div className="aud-json mt-2" ref={sqlRef}>
                <div className="aud-json-cabecera">
                  <span className="aud-json-titulo">
                    INSERT para la base de datos
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link py-0 px-1 text-decoration-none"
                    onClick={copiarSql}
                    title="Copiar al portapapeles"
                  >
                    {copiado ? (
                      <span className="text-success">
                        <i className="fas fa-check me-1"></i>Copiado
                      </span>
                    ) : (
                      <span>
                        <i className="far fa-copy me-1"></i>Copiar
                      </span>
                    )}
                  </button>
                </div>
                <pre>{sql}</pre>
              </div>
            ) : null}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="py-1">
        <span className="me-auto small text-muted">
          {totalSeleccionados} de {lista.length} registro(s) seleccionado(s)
        </span>
        <button
          type="button"
          className="btn btn-sm btn-success rounded-pill px-3"
          onClick={armarInsert}
          disabled={lista.length === 0}
          title="Armar el INSERT con las filas seleccionadas"
        >
          <i className="fas fa-code me-1"></i> Armar INSERT
        </button>
        <button
          className="btn btn-sm btn-primary rounded-pill px-4"
          onClick={onHideCancela}
        >
          Cerrar
        </button>
      </Modal.Footer>
    </Modal>
  );
};



// WEBPACK FOOTER //
// ./src/components/configuracionGeneral/modalRestaurarAuditoria.js